import { useMutation } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";

// =============================================================================
// Hooks pour la diffusion d'un changelog par email à tous les admin_aon.
// -----------------------------------------------------------------------------
// Délègue à l'edge function `changelog-broadcast` qui :
//   - Vérifie que l'appelant est admin_aon.
//   - Récupère la liste des admins en base.
//   - Si dry_run : retourne la liste des destinataires + le body.
//   - Sinon : envoie via Brevo, trace dans email_dispatches.
// =============================================================================

export interface ChangelogRecipient {
  user_id: string;
  email: string;
  prenom: string | null;
  nom: string | null;
}

export interface ChangelogBroadcastInput {
  version: string;
  subject: string;
  html: string;
  text?: string;
}

interface PreviewResponse {
  success: boolean;
  preview?: boolean;
  recipients?: ChangelogRecipient[];
  subject?: string;
  html?: string;
  error?: string;
}

interface SendResponse {
  success: boolean;
  sent?: number;
  failed?: number;
  total?: number;
  failed_recipients?: Array<{ email: string; reason: string }>;
  version?: string;
  error?: string;
}

/**
 * Récupère la liste des admins qui recevront le mail + l'aperçu HTML, sans
 * envoyer. Utilisé par le dialog de prévisualisation.
 */
export function usePreviewChangelogBroadcast() {
  return useMutation({
    mutationFn: async (
      input: ChangelogBroadcastInput,
    ): Promise<{ recipients: ChangelogRecipient[]; subject: string; html: string }> => {
      const { data, error } = await supabase.functions.invoke<PreviewResponse>(
        "changelog-broadcast",
        { body: { ...input, dry_run: true } },
      );
      if (error) {
        const ctx = (error as unknown as { context?: { error?: string } }).context;
        throw new Error(ctx?.error ?? error.message ?? "Erreur lors de la prévisualisation");
      }
      if (!data?.success || !data.recipients || !data.subject || !data.html) {
        throw new Error(data?.error ?? "Erreur lors de la prévisualisation");
      }
      return { recipients: data.recipients, subject: data.subject, html: data.html };
    },
  });
}

/**
 * Envoie effectivement l'email à tous les admins. Retourne le compte
 * (sent/failed) pour feedback utilisateur.
 */
export function useSendChangelogBroadcast() {
  return useMutation({
    mutationFn: async (
      input: ChangelogBroadcastInput,
    ): Promise<{
      sent: number;
      failed: number;
      total: number;
      failedRecipients: Array<{ email: string; reason: string }>;
    }> => {
      const { data, error } = await supabase.functions.invoke<SendResponse>("changelog-broadcast", {
        body: { ...input, dry_run: false },
      });
      if (error) {
        const ctx = (error as unknown as { context?: { error?: string } }).context;
        throw new Error(ctx?.error ?? error.message ?? "Erreur lors de l'envoi");
      }
      if (!data?.success) {
        throw new Error(data?.error ?? "Erreur lors de l'envoi");
      }
      return {
        sent: data.sent ?? 0,
        failed: data.failed ?? 0,
        total: data.total ?? 0,
        failedRecipients: data.failed_recipients ?? [],
      };
    },
  });
}
